/**
 * Resource Rundown configuration (spec RR-21.1, RR-21.2) — read + two saves.
 *
 * Reads go through `jsonFetch`, never a bare `fetch().then(r => r.json())`: an
 * API `{ error }` envelope read as success renders as `undefined` downstream and
 * crashes the panel instead of showing the error.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { jsonFetch } from "@/lib/json-fetch";
import { availabilityKeys, rundownConfigKeys } from "@/lib/query";

export interface RundownSettingsPayload {
  version: number;
  overThresholdPct: number;
  activeCeilingPct: number;
  gapThresholdPts: number;
  proposedDependentTolerancePts: number;
  winThresholdHours: number;
  standardMonthlyHours: number;
  /** Availability colours: booked % at or above this is Stretched (default 105). */
  stretchedThresholdPct: number;
  /** Availability colours: booked % at or above this is Fully booked (default 90). */
  fullyBookedFloorPct: number;
  /** Availability colours: booked % at or above this is Some room (default 75). */
  someRoomFloorPct: number;
}

export interface DisciplineDefault {
  disciplineId: string;
  disciplineName: string;
  targetPct: number | null;
}

export interface RundownConfig {
  settings: RundownSettingsPayload;
  disciplineDefaults: DisciplineDefault[];
}

/** The threshold fields, without the server-assigned version. */
export type RundownSettingsDraft = Omit<RundownSettingsPayload, "version">;

export function useRundownConfig() {
  return useQuery<RundownConfig>({
    queryKey: rundownConfigKeys.detail(),
    queryFn: () => jsonFetch<RundownConfig>("/api/rundown/config"),
  });
}

export function useUpdateRundownSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (draft: RundownSettingsDraft) =>
      jsonFetch<RundownSettingsPayload>("/api/rundown/config/settings", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(draft),
      }),
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: rundownConfigKeys.all });
      // The Availability cut-offs live in the same settings row.
      queryClient.invalidateQueries({ queryKey: availabilityKeys.all });
      toast.success(`Rundown thresholds saved as version ${saved.version}`);
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : "Could not save Rundown thresholds");
    },
  });
}

export function useUpdateDisciplineDefaults() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (defaults: Array<{ disciplineId: string; targetPct: number }>) =>
      jsonFetch<{ disciplineDefaults: DisciplineDefault[] }>(
        "/api/rundown/config/discipline-defaults",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ defaults }),
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rundownConfigKeys.all });
      toast.success("Billable Target defaults saved");
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : "Could not save Billable Target defaults");
    },
  });
}

/** The Home Roles the Availability report treats as non-delivery, and the roles to pick from. */
export interface NonDeliveryHomeRoles {
  /** Distinct Home Roles (NOS Titles) seen on synced people, sorted. */
  knownHomeRoles: string[];
  /** The Home Roles currently marked non-delivery, sorted. */
  nonDelivery: string[];
}

export function useNonDeliveryHomeRoles() {
  return useQuery<NonDeliveryHomeRoles>({
    queryKey: rundownConfigKeys.nonDeliveryHomeRoles(),
    queryFn: () => jsonFetch<NonDeliveryHomeRoles>("/api/rundown/config/non-delivery-home-roles"),
  });
}

export function useUpdateNonDeliveryHomeRoles() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (homeRoles: string[]) =>
      jsonFetch<NonDeliveryHomeRoles>("/api/rundown/config/non-delivery-home-roles", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ homeRoles }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rundownConfigKeys.all });
      queryClient.invalidateQueries({ queryKey: availabilityKeys.all });
      toast.success("Non-delivery Home Roles saved");
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : "Could not save non-delivery Home Roles");
    },
  });
}
